/**
 * useEventSubscriptions — All EventBus subscriptions for sounds and toasts
 *
 * Centralizes the 21 event subscriptions that were previously inline in App.tsx.
 * Sound events (5) + toast/UI feedback events (16).
 */

import { useEffect } from 'react';
import { eventBus } from '@/services/EventBus';
import { soundManager } from '@/services/SoundManager';
import { GameEvent } from '@/types';
import { useGameStore } from '@/state';
import { toast } from 'react-hot-toast';

export function useEventSubscriptions(): void {
  useEffect(() => {
    // ── Sound event listeners ──
    const unsubSoundCast = eventBus.on(GameEvent.FISHING_STARTED, () => {
      soundManager.playCastSplash();
    });

    const unsubSoundCatch = eventBus.on(GameEvent.FISH_CAUGHT, ({ fish }) => {
      soundManager.playCatchSuccess(fish.quality);
    });

    const unsubSoundMiss = eventBus.on(GameEvent.FISH_LOST, () => {
      soundManager.playCatchMiss();
    });

    const unsubSoundSell = eventBus.on(GameEvent.FISH_SOLD, () => {
      soundManager.playSellSound();
    });

    const unsubSoundQuest = eventBus.on(GameEvent.QUEST_COMPLETED, () => {
      soundManager.playQuestComplete();
    });

    // ── Toast event listeners ──
    const unsubFishCaught = eventBus.on(GameEvent.FISH_CAUGHT, ({ fish, definition }) => {
      // Only show toast when player is NOT on the fishing tab (overlay handles it there)
      const currentTab = useGameStore.getState().currentTab;
      if (currentTab !== 'fishing') {
        toast.success(`Caught a ${fish.quality} ${definition.name}!`, {
          icon: definition.emoji,
          duration: 2000,
        });
      }
    });

    const unsubFishLost = eventBus.on(GameEvent.FISH_LOST, () => {
      toast.error('The fish got away...', {
        icon: '💨',
        duration: 1500,
      });
    });

    const unsubInventoryFull = eventBus.on(GameEvent.INVENTORY_FULL, () => {
      toast.error('Inventory is full! Sell some fish.', {
        icon: '📦',
        duration: 3000,
      });
    });

    const unsubFishSold = eventBus.on(GameEvent.FISH_SOLD, (transaction) => {
      toast.success(`Sold fish for ${transaction.finalValue.toFixed(0)}g!`, {
        icon: '💰',
        duration: 2000,
      });
    });

    const unsubEquipmentPurchased = eventBus.on(GameEvent.EQUIPMENT_PURCHASED, () => {
      toast.success('Equipment purchased!', {
        icon: '🛒',
        duration: 2000,
      });
    });

    const unsubEquipmentEquipped = eventBus.on(GameEvent.EQUIPMENT_EQUIPPED, () => {
      toast.success('Equipment equipped!', {
        icon: '⚡',
        duration: 1500,
      });
    });

    const unsubGameSaved = eventBus.on(GameEvent.GAME_SAVED, () => {
      // Silent autosave, or show subtle notification
      console.log('💾 Game saved');
    });

    const unsubBuddyCaught = eventBus.on(GameEvent.BUDDY_CAUGHT_FISH, ({ buddySlot }) => {
      toast.success(`Buddy ${buddySlot} caught a fish!`, {
        icon: '🧑‍🤝‍🧑',
        duration: 2000,
      });
    });

    const unsubBuddyHired = eventBus.on(GameEvent.BUDDY_HIRED, ({ slotNumber, cost }) => {
      toast.success(`Hired Buddy ${slotNumber} for ${cost}g!`, {
        icon: '🧑‍🤝‍🧑',
        duration: 2000,
      });
    });

    const unsubQuestAccepted = eventBus.on(GameEvent.QUEST_ACCEPTED, ({ quest }) => {
      toast.success(`Quest accepted: ${quest.name}`, {
        icon: '📜',
        duration: 2000,
      });
    });

    const unsubQuestCompleted = eventBus.on(GameEvent.QUEST_COMPLETED, () => {
      toast.success('Quest completed!', {
        icon: '✅',
        duration: 2500,
      });
    });

    const unsubRewardsClaimed = eventBus.on(GameEvent.REWARDS_CLAIMED, () => {
      toast.success('Rewards claimed!', {
        icon: '🎁',
        duration: 2000,
      });
    });

    const unsubShopUnlocked = eventBus.on(GameEvent.SHOP_UNLOCKED, ({ shopType }) => {
      toast.success(`New ${shopType} unlocked!`, {
        icon: shopType === 'outfitter' ? '🏪' : '🍽️',
        duration: 2500,
      });
    });

    const unsubMilestone = eventBus.on(GameEvent.MILESTONE_ACHIEVED, ({ milestone }) => {
      const rewardText = milestone.rewards.map((r) => r.description).join(', ');
      toast.success(`${milestone.name} — ${rewardText}`, {
        icon: milestone.icon,
        duration: 4000,
      });
    });

    const unsubLevelUp = eventBus.on(GameEvent.PLAYER_LEVEL_UP, (payload) => {
      soundManager.playLevelUp();
      toast.success(`Level Up! You reached level ${payload.newLevel}!`, {
        icon: '🎉',
        duration: 3000,
      });
    });

    const unsubBuddyLevelUp = eventBus.on(GameEvent.BUDDY_LEVEL_UP, (payload) => {
      toast.success(`${payload.buddyName} reached level ${payload.newLevel}!`, {
        icon: '🎉',
        duration: 2500,
      });
    });

    // ── Trap event listeners ──
    const unsubTrapSet = eventBus.on(GameEvent.TRAP_SET, ({ soakDurationMs }) => {
      const minutes = Math.ceil(soakDurationMs / 60000);
      toast.success(`Trap set! Check back in ~${minutes} min.`, {
        icon: '🪤',
        duration: 2500,
      });
    });

    const unsubTrapReady = eventBus.on(GameEvent.TRAP_READY, () => {
      toast.success('Your trap is ready to pull!', {
        icon: '🪤',
        duration: 4000,
      });
    });

    const unsubTrapPulled = eventBus.on(GameEvent.TRAP_PULLED, (result) => {
      toast.success(`Pulled ${result.creatures.length} creatures worth ${result.totalValue}g!`, {
        icon: '🦞',
        duration: 3000,
      });
    });

    // Cleanup on unmount
    return () => {
      unsubSoundCast();
      unsubSoundCatch();
      unsubSoundMiss();
      unsubSoundSell();
      unsubSoundQuest();
      unsubFishCaught();
      unsubFishLost();
      unsubInventoryFull();
      unsubFishSold();
      unsubEquipmentPurchased();
      unsubEquipmentEquipped();
      unsubGameSaved();
      unsubBuddyCaught();
      unsubBuddyHired();
      unsubQuestAccepted();
      unsubQuestCompleted();
      unsubRewardsClaimed();
      unsubShopUnlocked();
      unsubMilestone();
      unsubLevelUp();
      unsubBuddyLevelUp();
      unsubTrapSet();
      unsubTrapReady();
      unsubTrapPulled();
    };
  }, []);
}
